import React, { useState, useEffect, useCallback } from 'react';
import {
  Database, Users, AlertTriangle, CheckCircle2, Trash2, RefreshCw,
  Download, Play, Eye, Server, Package, FileText, Loader2, ChevronRight,
  Shield, Activity, BarChart3, XCircle, Wrench
} from 'lucide-react';
import { API } from '../lib/api';


export default function DBManagementPage() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState('');
  const [results, setResults] = useState({});
  const [confirm, setConfirm] = useState(null); // { op, label }
  const [activeIds, setActiveIds] = useState([]);
  const [activating, setActivating] = useState(null); // clientId being activated
  const [activateResults, setActivateResults] = useState({}); // clientId → result

  const token = () => sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token') || '';

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/db-management/stats`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.ok) { setStats(d.stats); setActiveIds(d.activeClientIds || []); }
      else setResults(p => ({ ...p, statsError: d.error }));
    } catch (e) { setResults(p => ({ ...p, statsError: e.message })); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const activateClient = async (clientId) => {
    setActivating(clientId);
    try {
      const r = await fetch(`${API}/db-management/activate-pending-client`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
      });
      const d = await r.json();
      setActivateResults(p => ({ ...p, [clientId]: d }));
      if (d.ok) fetchStats();
    } catch (e) {
      setActivateResults(p => ({ ...p, [clientId]: { error: e.message } }));
    }
    setActivating(null);
  };

  const resendActivationEmail = async (clientId, email) => {
    const confirmed = window.confirm(
      `Send activation email to ${clientId}?\n\nThis will generate a new 72-hour activation link and send it to the registered email address${email ? ` (${email})` : ''}.\n\nAny previously sent link will be invalidated.`
    );
    if (!confirmed) return;
    setActivating(clientId + '_resend');
    try {
      const r = await fetch(`${API}/signup/resend-activation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
      });
      const d = await r.json();
      setActivateResults(p => ({ ...p, [clientId + '_resend']: d }));
    } catch (e) {
      setActivateResults(p => ({ ...p, [clientId + '_resend']: { error: e.message } }));
    }
    setActivating(null);
  };

  const runOp = async (op, dryRun = false) => {
    setConfirm(null);
    setRunning(op + (dryRun ? '_dry' : ''));
    try {
      const method = op === 'tsql' ? 'GET' : 'POST';
      if (op === 'tsql') {
        const r = await fetch(`${API}/db-management/tsql-script`, { headers: { Authorization: `Bearer ${token()}` } });
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `retailcliq-db-cleanup-${new Date().toISOString().slice(0,10)}.sql`;
        a.click(); URL.revokeObjectURL(url);
        setResults(p => ({ ...p, [op]: { ok: true, message: 'T-SQL script downloaded.' } }));
        setRunning('');
        return;
      }
      if (op === 'verify-physical-dbs') {
        const r = await fetch(`${API}/db-management/verify-physical-dbs`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        });
        const d = await r.json();
        setResults(p => ({ ...p, 'verify-physical-dbs': d }));
        setRunning('');
        return;
      }
      if (op === 'fix-data-quality' || op === 'fix-data-quality-dry') {
        const dryParam = op === 'fix-data-quality-dry';
        const r = await fetch(`${API}/db-management/fix-data-quality${dryParam ? '?dryRun=true' : ''}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        });
        const d = await r.json();
        setResults(p => ({ ...p, 'fix-data-quality': d }));
        setRunning('');
        if (!dryParam && d.ok) fetchStats();
        return;
      }
      const r = await fetch(`${API}/db-management/cleanup/${op}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun })
      });
      const d = await r.json();
      setResults(p => ({ ...p, [op + (dryRun ? '_dry' : '')]: d }));
      if (!dryRun && d.ok) fetchStats();
    } catch (e) {
      setResults(p => ({ ...p, [op]: { error: e.message } }));
    }
    setRunning('');
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = '#3b82f6', warn = false, testid }) => (
    <div data-testid={testid} style={{
      background: 'rgba(15,23,42,0.8)', border: `1px solid ${warn ? 'rgba(239,68,68,0.4)' : 'rgba(148,163,184,0.15)'}`,
      borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 11, fontWeight: 600 }}>
        <Icon size={13} color={warn ? '#f87171' : color} />
        {label}
        {warn && <span style={{ marginLeft: 'auto', padding: '1px 6px', borderRadius: 999, background: 'rgba(239,68,68,0.15)', color: '#f87171', fontSize: 9, fontWeight: 800 }}>NEEDS ATTENTION</span>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, color: warn ? '#f87171' : '#f1f5f9', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b' }}>{sub}</div>}
    </div>
  );

  const OpResult = ({ opKey, label }) => {
    const res = results[opKey];
    if (!res) return null;
    const isOk = res.ok || res.dryRun;
    const isErr = res.error;
    return (
      <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 6, fontSize: 11, background: isErr ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${isErr ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`, color: isErr ? '#f87171' : '#6ee7b7' }}>
        <strong>{isErr ? 'Error' : isOk ? 'Done' : 'Result'}:</strong>{' '}
        {isErr ? res.error : (
          res.message || JSON.stringify({ ...res, ok: undefined, dryRun: undefined }).slice(0, 200)
        )}
      </div>
    );
  };

  const hasAnyIssue = stats && (
    stats.childUsers.duplicates > 0 ||
    stats.registry.orphaned > 0 ||
    stats.telemetry.orphaned > 0 ||
    stats.licenses.stale > 0 ||
    Object.values(stats.poolDatabases || {}).some(schemas => schemas.some(s => s.canDrop))
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <Database size={22} color="#3b82f6" />
        <div>
          <h1 style={{ color: '#f1f5f9', fontSize: 20, margin: 0, fontWeight: 800 }}>DB Management</h1>
          <p style={{ color: '#64748b', fontSize: 11, margin: '3px 0 0' }}>
            Health dashboard · smart cleanup · T-SQL export — Active clients: {activeIds.join(', ') || 'loading…'}
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={fetchStats} disabled={loading} data-testid="db-refresh-btn" style={{ padding: '7px 14px', borderRadius: 7, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button onClick={() => runOp('tsql')} disabled={!!running} data-testid="db-tsql-btn" style={{ padding: '7px 14px', borderRadius: 7, background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', color: '#10b981', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={12} /> Export T-SQL Script
          </button>
        </div>
      </div>

      {/* Overall health banner */}
      {stats && (
        <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, background: hasAnyIssue ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${hasAnyIssue ? 'rgba(245,158,11,0.4)' : 'rgba(16,185,129,0.4)'}`, color: hasAnyIssue ? '#fbbf24' : '#10b981', fontSize: 12 }}>
          {hasAnyIssue ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
          {hasAnyIssue ? 'Issues detected — review the sections below and run the recommended cleanups.' : 'Database is healthy — all checks passed.'}
        </div>
      )}

      {loading && !stats && (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b', fontSize: 13 }}>
          <Loader2 size={20} className="spin" style={{ marginBottom: 12 }} /><br />Analysing database…
        </div>
      )}

      {stats && (
        <>
          {/* Stat Cards Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 28 }}>
            <StatCard icon={Server}     label="Active Clients"     value={stats.clientMaster.active}     sub={`${stats.clientMaster.deleted} soft-deleted`}                         color="#10b981" testid="stat-active-clients" />
            {stats.clientMaster.pending > 0 && (
              <StatCard icon={AlertTriangle} label="Pending Clients" value={stats.clientMaster.pending}  sub="Provisioning incomplete — run Fix Data Quality to activate"  warn={true}  testid="stat-pending-clients" />
            )}
            <StatCard icon={Users}      label="Child Users (total)" value={stats.childUsers.total}       sub="All ClientMaster_Child"                                               color="#3b82f6" testid="stat-child-total" />
            <StatCard icon={AlertTriangle} label="Duplicate Users"  value={stats.childUsers.duplicates}  sub="Duplicate (ClientId,UserType) pairs"  warn={stats.childUsers.duplicates > 0}  testid="stat-duplicates" />
            <StatCard icon={Package}    label="Total Licenses"      value={stats.licenses.total}          sub={`${stats.licenses.active} active`}                                   color="#a78bfa" testid="stat-licenses" />
            <StatCard icon={XCircle}    label="Stale Licenses"      value={stats.licenses.stale}          sub="Non-active for deleted clients"      warn={stats.licenses.stale > 0}           testid="stat-stale-lic" />
            <StatCard icon={Activity}   label="Registry Orphans"    value={stats.registry.orphaned}       sub={`of ${stats.registry.total} total`}  warn={stats.registry.orphaned > 0}        testid="stat-registry" />
            <StatCard icon={FileText}   label="Telemetry Orphans"   value={stats.telemetry.orphaned}      sub="ClientSignup_Metadata"               warn={stats.telemetry.orphaned > 0}       testid="stat-telemetry" />
          </div>

          {/* Pending Clients Activation Panel */}
          {(stats.pendingClients || []).length > 0 && (
            <div style={{ marginBottom: 20, borderRadius: 10, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.4)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(245,158,11,0.2)', flexWrap: 'wrap' }}>
                <AlertTriangle size={15} color="#f59e0b" />
                <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>Pending Tenants — Force Activate</span>
                <span style={{ fontSize: 11, color: '#92400e', background: 'rgba(245,158,11,0.2)', borderRadius: 12, padding: '2px 8px', fontWeight: 600 }}>
                  {stats.pendingClients.length} pending
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#92400e' }}>
                  Only activate after confirming provisioning completed
                </span>
              </div>
              <div style={{ padding: '12px 18px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(245,158,11,0.2)' }}>
                      {['Client ID', 'Name', 'Tier', 'DB Name', 'Users', 'Force Activate', 'Resend Email'].map(h => (
                        <th key={h} style={{ padding: '6px 8px', color: '#78350f', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.pendingClients.map(c => {
                      const res = activateResults[c.clientId];
                      return (
                        <tr key={c.clientId} style={{ borderBottom: '1px solid rgba(245,158,11,0.1)' }}>
                          <td style={{ padding: '8px', fontFamily: 'monospace', color: '#fbbf24', fontWeight: 700, fontSize: 10 }}>{c.clientId}</td>
                          <td style={{ padding: '8px', color: '#f1f5f9' }}>{c.clientName || '—'}</td>
                          <td style={{ padding: '8px', color: '#94a3b8', textTransform: 'uppercase', fontSize: 10 }}>{c.tierCode || '—'}</td>
                          <td style={{ padding: '8px', color: '#64748b', fontFamily: 'monospace', fontSize: 10 }}>{c.dbName || '—'}</td>
                          <td style={{ padding: '8px', color: c.childCount === 0 ? '#ef4444' : '#10b981', fontWeight: 700 }}>{c.childCount}</td>
                          <td style={{ padding: '8px' }}>
                            {res ? (
                              res.ok
                                ? <span style={{ color: '#10b981', fontSize: 10, fontWeight: 600 }}>✓ Activated</span>
                                : <span style={{ color: '#ef4444', fontSize: 10 }}>{res.error}</span>
                            ) : (
                              <button
                                data-testid={`activate-client-${c.clientId}`}
                                disabled={activating === c.clientId || c.childCount === 0}
                                onClick={() => activateClient(c.clientId)}
                                title={c.childCount === 0 ? 'No child users — run Fix Data Quality first' : 'Force activate this client'}
                                style={{
                                  padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                  cursor: (activating === c.clientId || c.childCount === 0) ? 'not-allowed' : 'pointer',
                                  background: c.childCount === 0 ? 'rgba(71,85,105,0.2)' : 'rgba(16,185,129,0.15)',
                                  border: `1px solid ${c.childCount === 0 ? '#334155' : 'rgba(16,185,129,0.5)'}`,
                                  color: c.childCount === 0 ? '#475569' : '#10b981',
                                  display: 'inline-flex', alignItems: 'center', gap: 4
                                }}>
                                {activating === c.clientId ? <Loader2 size={9} className="spin" /> : null}
                                {c.childCount === 0 ? 'No Users' : 'Force Activate'}
                              </button>
                            )}
                          </td>
                          {/* Resend Activation Email */}
                          <td style={{ padding: '8px' }}>
                            {activateResults[c.clientId + '_resend'] ? (
                              activateResults[c.clientId + '_resend'].ok
                                ? <div style={{ color: '#0ea5e9', fontSize: 10, fontWeight: 600, lineHeight: 1.4 }}>
                                    ✓ Email Sent
                                    {activateResults[c.clientId + '_resend'].expiresAt && (
                                      <div style={{ color: '#64748b', fontSize: 9, fontWeight: 400, marginTop: 2 }}>
                                        Expires: {new Date(activateResults[c.clientId + '_resend'].expiresAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    )}
                                  </div>
                                : <span style={{ color: '#ef4444', fontSize: 10 }}>{activateResults[c.clientId + '_resend'].error || 'Failed'}</span>
                            ) : (
                              <button
                                data-testid={`resend-activation-${c.clientId}`}
                                disabled={activating === c.clientId + '_resend'}
                                onClick={() => resendActivationEmail(c.clientId, c.email || c.adminEmail)}
                                title="Resend activation email to the registered email"
                                style={{
                                  padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                  cursor: 'pointer',
                                  background: 'rgba(14,165,233,0.1)',
                                  border: '1px solid rgba(14,165,233,0.4)',
                                  color: '#0ea5e9',
                                  display: 'inline-flex', alignItems: 'center', gap: 4
                                }}>
                                {activating === c.clientId + '_resend' ? <Loader2 size={9} className="spin" /> : null}
                                Resend Email
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(0,0,0,0.3)', fontSize: 10, color: '#92400e' }}>
                  <strong style={{ color: '#f59e0b' }}>Note:</strong> A client needs at least 1 child user before activation. If users are missing, run "Fix Data Quality" first.
                </div>
              </div>
            </div>
          )}

          {/* Section 1: Child User Deduplication */}
          <CleanupSection
            icon={Users} title="ClientMaster_Child — Duplicate Users"
            color="#3b82f6"
            status={stats.childUsers.duplicates > 0 ? 'warn' : 'ok'}
            statusText={stats.childUsers.duplicates > 0 ? `${stats.childUsers.duplicates} duplicate rows (keeping oldest per ClientId+UserType)` : 'No duplicates — clean'}
            actions={[
              { label: 'Preview duplicates (dry run)', op: 'dedup-children', dryRun: true, variant: 'secondary', testid: 'dry-run-dedup' },
              { label: 'Remove duplicates', op: 'dedup-children', dryRun: false, variant: 'danger', testid: 'run-dedup', confirm: true, confirmMsg: `Delete ${stats.childUsers.duplicates} duplicate child rows? Oldest records per (ClientId, UserType) will be retained.` }
            ]}
            results={results} runOp={runOp} running={running}
            setConfirm={setConfirm} confirm={confirm}
          />

          {/* Section 2: Stale License / Registry / Telemetry */}
          <CleanupSection
            icon={Package} title="LicenseRegistry · ClientMaster_License · Telemetry"
            color="#a78bfa"
            status={(stats.registry.orphaned + stats.telemetry.orphaned + stats.licenses.stale) > 0 ? 'warn' : 'ok'}
            statusText={`${stats.registry.orphaned} orphaned registry · ${stats.licenses.stale} stale licenses · ${stats.telemetry.orphaned} stale telemetry`}
            actions={[
              { label: 'Preview deletions (dry run)', op: 'stale-data', dryRun: true, variant: 'secondary', testid: 'dry-run-stale' },
              { label: 'Clean all stale data', op: 'stale-data', dryRun: false, variant: 'danger', testid: 'run-stale', confirm: true, confirmMsg: `Remove ${stats.registry.orphaned + stats.telemetry.orphaned + stats.licenses.stale} stale rows across LicenseRegistry, ClientMaster_License, and ClientSignup_Metadata?` }
            ]}
            results={results} runOp={runOp} running={running}
            setConfirm={setConfirm} confirm={confirm}
          />

          {/* Section 3: Pool DB Schemas */}
          <PoolSchemaSection stats={stats} results={results} running={running} runOp={runOp} setConfirm={setConfirm} confirm={confirm} />

          {/* Section 4: Enterprise DB */}
          <CleanupSection
            icon={Server} title="Enterprise DB — Stale S_Company / UserMaster"
            color="#f59e0b"
            status="neutral"
            statusText="Check each dedicated tenant DB for orphaned test companies and users"
            actions={[
              { label: 'Preview (dry run)', op: 'ent-db', dryRun: true, variant: 'secondary', testid: 'dry-run-ent' },
              { label: 'Clean enterprise DBs', op: 'ent-db', dryRun: false, variant: 'danger', testid: 'run-ent', confirm: true, confirmMsg: `Remove stale S_Company and UserMaster entries from dedicated Enterprise DBs?` }
            ]}
            results={results} runOp={runOp} running={running}
            setConfirm={setConfirm} confirm={confirm}
            resultRenderer={EntDbResult}
          />

          {/* Run All */}
          <div style={{ marginTop: 28, padding: 18, borderRadius: 10, background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(148,163,184,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Shield size={16} color="#3b82f6" />
              <div>
                <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 700 }}>Run All Cleanups</div>
                <div style={{ color: '#64748b', fontSize: 11 }}>Dedup children + stale data + enterprise DB cleanup (pool schema requires manual confirmation)</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <ActionBtn label="Preview all" op="all" dryRun={true} variant="secondary" testid="dry-run-all" runOp={runOp} running={running} setConfirm={setConfirm} confirm={confirm} />
                <ActionBtn label="Run all cleanups" op="all" dryRun={false} variant="danger" testid="run-all" runOp={runOp} running={running}
                  setConfirm={setConfirm} confirm={confirm}
                  confirmMsg="Run complete cleanup? This will remove duplicate child records, stale licenses, stale registry, and stale telemetry." />
              </div>
            </div>
            <OpResult opKey="all" />
            <OpResult opKey="all_dry" label="Preview" />
            <OpResult opKey="tsql" />
          </div>

          {/* Fix Data Quality */}
          <div style={{ marginTop: 16, padding: 18, borderRadius: 10, background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(99,102,241,0.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Wrench size={16} color="#818cf8" />
              <div>
                <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 700 }}>Fix Data Quality</div>
                <div style={{ color: '#64748b', fontSize: 11 }}>
                  Normalises legacy rows: UserType, IpAddress, HBS dates, CStatus, ClientPassword, AltPassword.
                  Sets default password (aaaaaa12) for zombie records with empty passwords.
                  Auto-activates PENDING clients that have been fully provisioned.
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button
                  data-testid="fix-data-quality-dry-btn"
                  disabled={!!running}
                  onClick={() => runOp('fix-data-quality-dry')}
                  style={{ padding: '7px 12px', borderRadius: 7, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8', cursor: running ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600 }}>
                  {running === 'fix-data-quality-dry' ? <Loader2 size={11} className="spin" /> : 'Preview'}
                </button>
                <button
                  data-testid="fix-data-quality-btn"
                  disabled={!!running}
                  onClick={() => runOp('fix-data-quality')}
                  style={{ padding: '7px 14px', borderRadius: 7, background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.5)', color: '#a5b4fc', cursor: running ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {running === 'fix-data-quality' ? <><Loader2 size={11} className="spin" /> Fixing…</> : <><Wrench size={11} /> Fix Now</>}
                </button>
              </div>
            </div>
            {results['fix-data-quality'] && (
              <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: 'rgba(0,0,0,0.3)', fontSize: 11 }}>
                <div style={{ color: results['fix-data-quality'].ok ? '#10b981' : '#ef4444', marginBottom: 6, fontWeight: 600 }}>
                  {results['fix-data-quality'].dryRun ? 'Preview' : `Fixed — ${results['fix-data-quality'].totalRowsAffected} rows updated`}
                </div>
                {(results['fix-data-quality'].steps || []).map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 2, color: s.status === 'ERROR' ? '#ef4444' : s.rowsAffected > 0 ? '#a5b4fc' : '#475569' }}>
                    {s.status === 'ERROR' ? <XCircle size={10} /> : s.rowsAffected > 0 ? <CheckCircle2 size={10} color="#a5b4fc" /> : <span style={{ width: 10 }}>·</span>}
                    <span>{s.label}: {s.status === 'DRY_RUN' ? 'would run' : s.status === 'ERROR' ? s.error : `${s.rowsAffected} rows`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginTop: 16, padding: 18, borderRadius: 10, background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(234,179,8,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Server size={16} color="#eab308" />
              <div>
                <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 700 }}>Verify Core Physical Databases</div>
                <div style={{ color: '#64748b', fontSize: 11 }}>Checks RC26ERP_APPAREL_001 and RC26ERP_IN_ENT_0003 exist on SQL Server. If either is missing, auto-recreates from RC26ERP_DEV and sends an alert email to admin.</div>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <button
                  data-testid="verify-physical-dbs-btn"
                  disabled={!!running}
                  onClick={() => runOp('verify-physical-dbs')}
                  style={{ padding: '7px 14px', borderRadius: 7, background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.4)', color: '#eab308', cursor: running ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {running === 'verify-physical-dbs' ? <><Loader2 size={11} className="spin" /> Checking…</> : <><Server size={11} /> Check Now</>}
                </button>
              </div>
            </div>
            {results['verify-physical-dbs'] && (
              <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: 'rgba(0,0,0,0.3)', fontSize: 11 }}>
                {(results['verify-physical-dbs'].results || []).map(r => (
                  <div key={r.db} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {r.status === 'EXISTS'     && <CheckCircle2 size={11} color="#10b981" />}
                    {r.status === 'RECREATED'  && <Activity     size={11} color="#f59e0b" />}
                    {(r.status === 'FAILED' || r.status === 'ERROR') && <AlertTriangle size={11} color="#ef4444" />}
                    <span style={{ color: r.status === 'EXISTS' ? '#10b981' : r.status === 'RECREATED' ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>{r.db}</span>
                    <span style={{ color: '#94a3b8' }}>({r.label})</span>
                    <span style={{ color: '#475569' }}>— {r.status}{r.durationMs ? ` in ${r.durationMs}ms` : ''}{r.error ? `: ${r.error}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation modal */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: 12, padding: 28, maxWidth: 440, width: '90%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <AlertTriangle size={18} color="#f87171" />
              <strong style={{ color: '#f1f5f9' }}>Confirm Cleanup</strong>
            </div>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 20px', lineHeight: 1.6 }}>{confirm.confirmMsg}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirm(null)} style={{ padding: '8px 18px', borderRadius: 7, background: 'transparent', border: '1px solid #475569', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>
                Cancel
              </button>
              <button data-testid={confirm.testid + '-confirm'} onClick={() => runOp(confirm.op, false)} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(239,68,68,0.2)', border: '1px solid #ef4444', color: '#f87171', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CleanupSection ─────────────────────────────────────────────────────────
function CleanupSection({ icon: Icon, title, color, status, statusText, actions, results, runOp, running, setConfirm, confirm, resultRenderer: ResultRenderer }) {
  const isWarn = status === 'warn';
  const isOk   = status === 'ok';
  return (
    <div style={{ marginBottom: 16, borderRadius: 10, background: 'rgba(15,23,42,0.8)', border: `1px solid ${isWarn ? 'rgba(239,68,68,0.25)' : 'rgba(148,163,184,0.12)'}`, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
        <Icon size={15} color={isWarn ? '#f87171' : isOk ? '#10b981' : color} />
        <span style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 700 }}>{title}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: isWarn ? '#f87171' : '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
          {isWarn ? <AlertTriangle size={11}/> : <CheckCircle2 size={11}/>} {statusText}
        </span>
      </div>
      <div style={{ padding: '12px 18px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {actions.map(a => (
          <ActionBtn key={a.op + a.dryRun} {...a} runOp={runOp} running={running} setConfirm={setConfirm} confirm={confirm} />
        ))}
      </div>
      {ResultRenderer
        ? actions.map(a => {
            const key = a.op + (a.dryRun ? '_dry' : '');
            return results[key] ? <div key={key} style={{ padding: '0 18px 8px' }}><ResultRenderer result={results[key]} /></div> : null;
          })
        : actions.map(a => <div key={a.op + a.dryRun} style={{ padding: '0 18px 8px' }}><OpResult_ opKey={a.op + (a.dryRun ? '_dry' : '')} results={results} /></div>)
      }
    </div>
  );
}

function PoolSchemaSection({ stats, results, running, runOp, setConfirm, confirm }) {
  const schemas = Object.entries(stats.poolDatabases || {}).flatMap(([db, list]) => list.map(s => ({ ...s, db })));
  const orphaned = schemas.filter(s => s.canDrop);
  const hasData  = schemas.filter(s => !s.isKept && s.hasFunctionalData);
  const kept     = schemas.filter(s => s.isKept);

  return (
    <div style={{ marginBottom: 16, borderRadius: 10, background: 'rgba(15,23,42,0.8)', border: `1px solid ${orphaned.length > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(148,163,184,0.12)'}`, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
        <BarChart3 size={15} color={orphaned.length > 0 ? '#f87171' : '#10b981'} />
        <span style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 700 }}>Pool DB Schemas</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: orphaned.length > 0 ? '#f87171' : '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
          {orphaned.length > 0 ? <AlertTriangle size={11}/> : <CheckCircle2 size={11}/>}
          {orphaned.length} orphaned · {hasData.length} protected (has data) · {kept.length} active
        </span>
      </div>
      {schemas.length > 0 && (
        <div style={{ padding: '12px 18px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                {['DB', 'Schema', 'Status', 'Has Transactions', 'Action'].map(h => (
                  <th key={h} style={{ padding: '6px 8px', color: '#64748b', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schemas.map(s => (
                <tr key={s.db + s.schema} style={{ borderBottom: '1px solid rgba(148,163,184,0.06)' }}>
                  <td style={{ padding: '6px 8px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 10 }}>{s.db}</td>
                  <td style={{ padding: '6px 8px', color: '#f1f5f9', fontFamily: 'monospace' }}>{s.schema}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: 9, fontWeight: 700,
                      background: s.isKept ? 'rgba(16,185,129,0.15)' : s.hasFunctionalData ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                      color: s.isKept ? '#10b981' : s.hasFunctionalData ? '#fbbf24' : '#f87171',
                      border: `1px solid ${s.isKept ? '#10b981' : s.hasFunctionalData ? '#f59e0b' : '#ef4444'}`
                    }}>
                      {s.isKept ? 'ACTIVE' : s.hasFunctionalData ? 'PROTECTED' : 'ORPHANED'}
                    </span>
                  </td>
                  <td style={{ padding: '6px 8px', color: s.hasFunctionalData ? '#fbbf24' : '#64748b' }}>{s.hasFunctionalData ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '6px 8px', color: '#64748b', fontSize: 10 }}>
                    {s.isKept ? '—' : s.hasFunctionalData ? 'Manual only (see T-SQL)' : 'Will be dropped on cleanup'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ padding: '0 18px 14px', display: 'flex', gap: 8 }}>
        <ActionBtn label="Preview schema cleanup" op="pool-schemas" dryRun={true} variant="secondary" testid="dry-run-pool" runOp={runOp} running={running} setConfirm={setConfirm} confirm={confirm} />
        <ActionBtn
          label={orphaned.length > 0 ? `Drop ${orphaned.length} orphaned schemas` : 'No orphaned schemas'}
          op="pool-schemas" dryRun={false} variant="danger" testid="run-pool"
          runOp={orphaned.length > 0 ? runOp : () => {}} running={running}
          setConfirm={orphaned.length > 0 ? setConfirm : () => {}} confirm={confirm}
          confirmMsg={`Drop ${orphaned.length} orphaned pool DB schemas? Only empty schemas (no transactions) will be dropped.`}
          disabled={orphaned.length === 0}
        />
      </div>
      <div style={{ padding: '0 18px 8px' }}><OpResult_ opKey="pool-schemas" results={results} /></div>
      <div style={{ padding: '0 18px 8px' }}><OpResult_ opKey="pool-schemas_dry" results={results} /></div>
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────
function ActionBtn({ label, op, dryRun, variant, testid, runOp, running, setConfirm, confirm, confirmMsg, disabled }) {
  const isRunning = running === (op + (dryRun ? '_dry' : ''));
  const styles = {
    secondary: { background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.35)', color: '#60a5fa' },
    danger:    { background: 'rgba(239,68,68,0.12)',  border: '1px solid rgba(239,68,68,0.35)',  color: '#f87171' },
  };
  const s = styles[variant] || styles.secondary;
  const isDisabled = disabled || !!running;

  const handleClick = () => {
    if (disabled) return;
    if (!dryRun && confirmMsg) {
      setConfirm({ op, confirmMsg, testid });
    } else {
      runOp(op, dryRun);
    }
  };

  return (
    <button onClick={handleClick} disabled={isDisabled} data-testid={testid} style={{
      padding: '7px 14px', borderRadius: 7, cursor: isDisabled ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600,
      display: 'inline-flex', alignItems: 'center', gap: 6, opacity: disabled ? 0.5 : 1, ...s
    }}>
      {isRunning ? <Loader2 size={11} className="spin" /> : dryRun ? <Eye size={11} /> : <Play size={11} />}
      {isRunning ? 'Running…' : label}
    </button>
  );
}

// ── Enterprise DB dry-run/result renderer ──────────────────────────────────
function EntDbResult({ result }) {
  if (!result) return null;
  const isErr = result.error;
  if (isErr) return (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171' }}>
      <strong>Error:</strong> {result.error}
    </div>
  );
  const rows = result.results || [];
  if (rows.length === 0) return (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7' }}>
      No enterprise DBs found.
    </div>
  );
  return (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, background: result.dryRun ? 'rgba(59,130,246,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${result.dryRun ? 'rgba(59,130,246,0.3)' : 'rgba(16,185,129,0.3)'}` }}>
      <div style={{ color: result.dryRun ? '#60a5fa' : '#6ee7b7', fontWeight: 700, marginBottom: 6 }}>{result.dryRun ? 'Preview — no changes made' : 'Cleanup complete'}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
            {['DB', 'ClientId', result.dryRun ? 'Stale Companies' : 'Companies Deleted', result.dryRun ? 'Stale Users' : 'Users Deleted'].map(h => (
              <th key={h} style={{ padding: '4px 8px', color: '#64748b', textAlign: 'left', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.db} style={{ borderBottom: '1px solid rgba(148,163,184,0.06)' }}>
              <td style={{ padding: '4px 8px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 10 }}>{r.db}</td>
              <td style={{ padding: '4px 8px', color: '#f1f5f9' }}>{r.clientId}</td>
              <td style={{ padding: '4px 8px', color: (result.dryRun ? r.willDeleteCompanies : r.deletedCompanies) > 0 ? '#fbbf24' : '#10b981' }}>
                {result.dryRun ? r.willDeleteCompanies : r.deletedCompanies ?? 0}
              </td>
              <td style={{ padding: '4px 8px', color: (result.dryRun ? r.willDeleteUsers : r.deletedUsers) > 0 ? '#fbbf24' : '#10b981' }}>
                {result.dryRun ? r.willDeleteUsers : r.deletedUsers ?? 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpResult_({ opKey, results }) {
  const res = results?.[opKey];
  if (!res) return null;
  const isErr = res.error;
  return (
    <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, background: isErr ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)', border: `1px solid ${isErr ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`, color: isErr ? '#f87171' : '#6ee7b7' }}>
      <strong>{isErr ? 'Error' : res.dryRun ? 'Preview' : 'Done'}:</strong>{' '}
      {isErr ? res.error : (res.message || JSON.stringify({ ...res, ok: undefined, dryRun: undefined }).slice(0, 300))}
    </div>
  );
}
